import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Save, 
  Loader2, 
  Gift, 
  Calendar, 
  Monitor, 
  Smartphone,
  Link as LinkIcon,
  Image,
  Clock,
  Eye,
  MousePointer,
  X,
  Plus,
  Palette,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromoEditor = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Route chip input states
  const [newShowRoute, setNewShowRoute] = useState('');
  const [newHideRoute, setNewHideRoute] = useState('');

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/landing/promo-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        toast.error('Failed to load promo configuration');
      }
    } catch (error) {
      console.error('Error fetching promo config:', error);
      toast.error('Failed to load promo configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Unsaved changes guard
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Update config field
  const updateField = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Save config
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/landing/promo-config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(config),
      });
      
      if (response.ok) {
        toast.success('Promo configuration saved');
        setHasChanges(false);
        // Refetch to confirm persistence
        await fetchConfig();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save promo configuration');
      }
    } catch (error) {
      console.error('Error saving promo config:', error);
      toast.error('Failed to save promo configuration');
    } finally {
      setSaving(false);
    }
  };

  // Upload image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'promo_image');
      formData.append('item_id', config.campaignId || 'default');
      
      const response = await fetch(`${API_URL}/api/landing/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        updateField('imageUrl', data.image_url);
        toast.success('Image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Add route chip
  const addRouteChip = (type) => {
    const value = type === 'show' ? newShowRoute : newHideRoute;
    if (!value.trim()) return;
    
    const route = value.startsWith('/') ? value : `/${value}`;
    const field = type === 'show' ? 'showOnRoutes' : 'hideOnRoutes';
    const current = config[field] || [];
    
    if (!current.includes(route)) {
      updateField(field, [...current, route]);
    }
    
    if (type === 'show') setNewShowRoute('');
    else setNewHideRoute('');
  };

  // Remove route chip
  const removeRouteChip = (type, route) => {
    const field = type === 'show' ? 'showOnRoutes' : 'hideOnRoutes';
    const current = config[field] || [];
    updateField(field, current.filter(r => r !== route));
  };

  // Toggle device
  const toggleDevice = (device) => {
    const current = config.showOn || [];
    if (current.includes(device)) {
      updateField('showOn', current.filter(d => d !== device));
    } else {
      updateField('showOn', [...current, device]);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Failed to load configuration</p>
        <Button onClick={fetchConfig} className="mt-4">Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Gift className="w-6 h-6 text-primary" />
            Promo Banner Editor
          </h1>
          <p className="text-slate-500 mt-1">
            Configure holiday promotions and announcement banners
          </p>
        </div>
        <div className="flex items-center gap-3">
          {hasChanges && (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              Unsaved changes
            </Badge>
          )}
          <Button onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Main Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Settings</CardTitle>
            <CardDescription>Enable and configure your promotion</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enabled Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Promo</Label>
                <p className="text-sm text-slate-500">Show promotion banner to visitors</p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => updateField('enabled', checked)}
                data-testid="promo-enabled-toggle"
              />
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label>Display Type</Label>
              <Select value={config.type} onValueChange={(value) => updateField('type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popup">Poster Popup (Image-First)</SelectItem>
                  <SelectItem value="topbar">Announcement Bar (Top)</SelectItem>
                  <SelectItem value="popup_topbar">Both (Popup + Announcement Bar)</SelectItem>
                  <SelectItem value="floating">Legacy Floating Banner (Bottom)</SelectItem>
                  <SelectItem value="both">Legacy Both (Floating + Topbar)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                {config.type === 'popup' && 'Shows uploaded flyer as dominant visual in a centered popup'}
                {config.type === 'topbar' && 'Slim announcement bar at the top of the page'}
                {config.type === 'popup_topbar' && 'Popup + slim top bar for maximum visibility'}
                {config.type === 'floating' && 'Banner fixed to bottom of screen (legacy)'}
                {config.type === 'both' && 'Floating banner + top bar (legacy)'}
              </p>
            </div>

            {/* Theme */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Theme
              </Label>
              <Select value={config.theme} onValueChange={(value) => updateField('theme', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="navy">Navy (Dark Blue)</SelectItem>
                  <SelectItem value="aqua">Aqua (Teal)</SelectItem>
                  <SelectItem value="default">Default (Primary)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Campaign ID & Variant */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campaign ID</Label>
                <Input
                  value={config.campaignId}
                  onChange={(e) => updateField('campaignId', e.target.value)}
                  placeholder="holiday_2026_v1"
                />
              </div>
              <div className="space-y-2">
                <Label>Variant</Label>
                <Input
                  value={config.variant}
                  onChange={(e) => updateField('variant', e.target.value)}
                  placeholder="A"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Content */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Content</CardTitle>
            <CardDescription>Promo title, message, and CTAs</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={config.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="Holiday Swimming Program"
              />
            </div>

            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea
                value={config.message}
                onChange={(e) => updateField('message', e.target.value)}
                placeholder="Registration is open. Limited slots available."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Primary CTA Text</Label>
                <Input
                  value={config.primaryCtaText}
                  onChange={(e) => updateField('primaryCtaText', e.target.value)}
                  placeholder="Enroll Now"
                />
              </div>
              <div className="space-y-2">
                <Label>Primary CTA Link</Label>
                <Input
                  value={config.primaryCtaLink}
                  onChange={(e) => updateField('primaryCtaLink', e.target.value)}
                  placeholder="/holiday-program"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Secondary CTA Text</Label>
                <Input
                  value={config.secondaryCtaText}
                  onChange={(e) => updateField('secondaryCtaText', e.target.value)}
                  placeholder="WhatsApp"
                />
              </div>
              <div className="space-y-2">
                <Label>Secondary CTA Link</Label>
                <Input
                  value={config.secondaryCtaLink}
                  onChange={(e) => updateField('secondaryCtaLink', e.target.value)}
                  placeholder="Leave empty for global WhatsApp"
                />
                <p className="text-xs text-slate-400">Empty = uses global preset</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Image Upload */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Image className="w-5 h-5" />
              Promo Image
            </CardTitle>
            <CardDescription>Optional promotional image</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {config.imageUrl && (
              <div className="relative">
                <img
                  src={config.imageUrl.startsWith('/') ? `${API_URL}${config.imageUrl}` : config.imageUrl}
                  alt="Promo"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <Button
                  variant="destructive"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => updateField('imageUrl', '')}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            
            <div>
              <Label htmlFor="promo-image-upload" className="cursor-pointer">
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-primary transition-colors">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary" />
                  ) : (
                    <>
                      <Image className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Click to upload image</p>
                    </>
                  )}
                </div>
              </Label>
              <input
                id="promo-image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
                disabled={uploading}
              />
            </div>
          </CardContent>
        </Card>

        {/* Scheduling */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Scheduling
            </CardTitle>
            <CardDescription>Date range and display timing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="datetime-local"
                  value={config.startAt ? config.startAt.slice(0, 16) : ''}
                  onChange={(e) => updateField('startAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="datetime-local"
                  value={config.endAt ? config.endAt.slice(0, 16) : ''}
                  onChange={(e) => updateField('endAt', e.target.value ? new Date(e.target.value).toISOString() : null)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Delay (ms)
                </Label>
                <Input
                  type="number"
                  value={config.delayMs}
                  onChange={(e) => updateField('delayMs', parseInt(e.target.value) || 0)}
                  min={0}
                  step={500}
                />
                <p className="text-xs text-slate-400">Time before showing banner</p>
              </div>
              <div className="space-y-2">
                <Label>Frequency (days)</Label>
                <Input
                  type="number"
                  value={config.frequencyDays}
                  onChange={(e) => updateField('frequencyDays', parseInt(e.target.value) || 1)}
                  min={1}
                />
                <p className="text-xs text-slate-400">Don't show again for X days</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Frequency Mode</Label>
              <Select value={config.frequencyMode} onValueChange={(value) => updateField('frequencyMode', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dismiss">On Dismiss</SelectItem>
                  <SelectItem value="view">On View (Impression)</SelectItem>
                  <SelectItem value="click">On CTA Click</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Targeting */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Targeting</CardTitle>
            <CardDescription>Device and route targeting</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Device Targeting */}
            <div className="space-y-2">
              <Label>Show On Devices</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.showOn?.includes('desktop')}
                    onChange={() => toggleDevice('desktop')}
                    className="rounded border-slate-300"
                  />
                  <Monitor className="w-4 h-4" />
                  Desktop
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.showOn?.includes('mobile')}
                    onChange={() => toggleDevice('mobile')}
                    className="rounded border-slate-300"
                  />
                  <Smartphone className="w-4 h-4" />
                  Mobile
                </label>
              </div>
            </div>

            {/* Show On Routes */}
            <div className="space-y-2">
              <Label>Show On Routes</Label>
              <div className="flex gap-2">
                <Input
                  value={newShowRoute}
                  onChange={(e) => setNewShowRoute(e.target.value)}
                  placeholder="/programs"
                  onKeyDown={(e) => e.key === 'Enter' && addRouteChip('show')}
                />
                <Button variant="outline" size="icon" onClick={() => addRouteChip('show')}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {config.showOnRoutes?.map((route) => (
                  <Badge key={route} variant="secondary" className="gap-1">
                    {route}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => removeRouteChip('show', route)}
                    />
                  </Badge>
                ))}
                {(!config.showOnRoutes || config.showOnRoutes.length === 0) && (
                  <span className="text-xs text-slate-400">Empty = show on all routes</span>
                )}
              </div>
            </div>

            {/* Hide On Routes */}
            <div className="space-y-2">
              <Label>Hide On Routes</Label>
              <div className="flex gap-2">
                <Input
                  value={newHideRoute}
                  onChange={(e) => setNewHideRoute(e.target.value)}
                  placeholder="/checkout"
                  onKeyDown={(e) => e.key === 'Enter' && addRouteChip('hide')}
                />
                <Button variant="outline" size="icon" onClick={() => addRouteChip('hide')}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {config.hideOnRoutes?.map((route) => (
                  <Badge key={route} variant="outline" className="gap-1 text-red-600 border-red-200">
                    {route}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => removeRouteChip('hide', route)}
                    />
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tracking */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MousePointer className="w-5 h-5" />
              Tracking
            </CardTitle>
            <CardDescription>Analytics and event tracking</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Enable Tracking</Label>
                <p className="text-sm text-slate-500">Track impressions, clicks, and dismisses</p>
              </div>
              <Switch
                checked={config.trackingEnabled}
                onCheckedChange={(checked) => updateField('trackingEnabled', checked)}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Preview
          </CardTitle>
          <CardDescription>See how your promo will look</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-100 rounded-lg p-4 min-h-[120px] relative overflow-hidden">
            {config.enabled ? (
              <>
                {/* Popup Preview */}
                {(config.type === 'popup' || config.type === 'popup_topbar') && (
                  <div className="bg-slate-900 rounded-xl overflow-hidden max-w-sm mx-auto shadow-2xl">
                    {config.imageUrl && (
                      <img
                        src={config.imageUrl.startsWith('/') ? `${API_URL}${config.imageUrl}` : config.imageUrl}
                        alt="Promo"
                        className="w-full h-auto object-contain"
                        style={{ maxHeight: '200px' }}
                      />
                    )}
                    <div className="p-4 space-y-3 bg-gradient-to-b from-slate-900 to-slate-800">
                      {config.message && (
                        <p className="text-white/90 text-sm text-center">{config.message}</p>
                      )}
                      <div className="py-2.5 px-4 bg-cyan-500 text-slate-900 font-bold text-center rounded-lg text-sm">
                        {config.primaryCtaText}
                      </div>
                    </div>
                  </div>
                )}

                {/* Topbar Preview */}
                {(config.type === 'topbar' || config.type === 'popup_topbar') && (
                  <div className={`
                    ${config.theme === 'navy' ? 'bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900' : ''}
                    ${config.theme === 'aqua' ? 'bg-gradient-to-r from-cyan-600 via-teal-500 to-cyan-600' : ''}
                    ${config.theme === 'default' ? 'bg-gradient-to-r from-primary via-primary/90 to-primary' : ''}
                    text-white py-2 px-4 rounded-lg text-sm flex items-center justify-center gap-3
                    ${config.type === 'popup_topbar' ? 'mt-4' : ''}
                  `}>
                    <Gift className="w-4 h-4 text-cyan-400" />
                    <span className="font-medium">{config.title}</span>
                    <span className="opacity-75">—</span>
                    <span className="opacity-90">{config.message}</span>
                    <span className="px-3 py-1 bg-cyan-500 text-slate-900 rounded-full text-xs font-semibold">
                      {config.primaryCtaText}
                    </span>
                  </div>
                )}

                {/* Legacy Floating Banner Preview */}
                {(config.type === 'floating' || config.type === 'both') && (
                  <div className={`
                    ${config.theme === 'navy' ? 'bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900' : ''}
                    ${config.theme === 'aqua' ? 'bg-gradient-to-r from-cyan-600 via-teal-500 to-cyan-600' : ''}
                    ${config.theme === 'default' ? 'bg-gradient-to-r from-primary via-primary/90 to-primary' : ''}
                    text-white p-4 rounded-lg
                    ${config.type === 'both' ? 'mt-4' : ''}
                  `}>
                    <div className="flex items-center gap-4">
                      <Gift className="w-8 h-8 text-cyan-400" />
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-cyan-400">{config.title}</h3>
                        <p className="text-sm opacity-90">{config.message}</p>
                      </div>
                      <div className="flex gap-2">
                        <span className="px-4 py-2 bg-cyan-500 text-slate-900 rounded-lg font-semibold text-sm">
                          {config.primaryCtaText}
                        </span>
                        {config.secondaryCtaText && (
                          <span className="px-4 py-2 bg-white/10 rounded-lg text-sm">
                            {config.secondaryCtaText}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-20 text-slate-400">
                <p>Enable promo to see preview</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PromoEditor;
